/**
 * @file useRecorderAgendaChecks.ts
 * @brief Loads the recorder's agenda items into its tick list (B49).
 *
 * @details
 * Fetches `GET /agenda-items` for the recorder's agenda notes when the
 * recording starts and again whenever the notes change (agenda picker in the
 * recorder), keeping the ticks of items still listed. On a network / server
 * error the cached agenda notes are split here instead (the server matches by
 * wording and position, so both work). A 404 means the switch is off: the
 * list goes back to `null` and the agenda stays read-only, as before.
 *
 * @references agendaChecks.ts, docs/product/agenda-checks-b49.md
 *
 * @author DES
 * @date 02-11-2026
 */

import { useEffect } from "react";
import { z } from "zod";
import { useQueries, useQuery } from "@tanstack/react-query";
import { ApiClientError, apiGet } from "@/shared/api/client";
import { boardroomEndpoints, notesEndpoints } from "@/shared/api/endpoints";
import { notesKeys } from "@/features/notes/api";
import { NoteResponseSchema, notePointLevels, notePoints, type VoiceNote } from "@/features/notes/schemas";
import {
  mergeAgendaItems,
  splitAgendaNote,
  type AgendaCheck,
  type AgendaItemsNote,
} from "./agendaChecks";

const AgendaItemsResponseSchema = z.object({
  success: z.literal(true),
  notes: z.array(
    z.object({
      noteId: z.string(),
      noteTitle: z.string().nullable().optional(),
      items: z.array(
        z.object({
          index: z.number(),
          text: z.string(),
          parentText: z.string().nullable().optional(),
        }),
      ),
    }),
  ),
});

/** `null` = the switch is off (404): show the agenda read-only as before. */
export async function fetchAgendaItems(noteIds: string[]): Promise<AgendaItemsNote[] | null> {
  try {
    const raw = await apiGet<unknown>(boardroomEndpoints.agendaItems(noteIds));
    return AgendaItemsResponseSchema.parse(raw).notes.map((n) => ({
      noteId: n.noteId,
      noteTitle: n.noteTitle?.trim() || "Agenda",
      items: n.items,
    }));
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return null;
    throw err;
  }
}

export function useRecorderAgendaChecks({
  noteIds,
  enabled,
  draftId,
  setAgendaChecks,
}: {
  noteIds: string[];
  /** Off for Meeting Notes and once the recording has stopped. */
  enabled: boolean;
  /** Re-run once the draft exists (the recorder ignores writes before it). */
  draftId: string | null;
  setAgendaChecks: (update: (prev: AgendaCheck[] | null) => AgendaCheck[] | null) => void;
}): void {
  const key = noteIds.join(",");
  const active = enabled && !!draftId && noteIds.length > 0;
  const items = useQuery({
    queryKey: ["boardroom", "agenda-items", key],
    enabled: active,
    queryFn: () => fetchAgendaItems(noteIds),
    retry: 1,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Fallback only: the recorder's agenda panel already holds these notes, so
  // this is normally a cache read.
  const notes = useQueries({
    queries: noteIds.map((id) => ({
      queryKey: notesKeys.detail(id),
      enabled: active && items.isError,
      queryFn: async (): Promise<VoiceNote> =>
        NoteResponseSchema.parse(await apiGet<unknown>(notesEndpoints.byId(id))).note,
    })),
  });
  const notesReady = notes.length > 0 && notes.every((n) => !!n.data);
  const notesKey = notesReady ? notes.map((n) => n.dataUpdatedAt).join(",") : "";

  useEffect(() => {
    if (!enabled || !draftId) return;
    if (noteIds.length === 0 || items.data === null) {
      setAgendaChecks(() => null);
      return;
    }
    let list: AgendaItemsNote[] | null = null;
    if (items.data) {
      const byId = new Map(items.data.map((n) => [n.noteId, n]));
      list = noteIds.map((id) => byId.get(id)).filter((n): n is AgendaItemsNote => !!n);
    } else if (items.isError && notesReady) {
      list = notes.map((n, i) => {
        const note = n.data as VoiceNote;
        const points = notePoints(note);
        return splitAgendaNote(noteIds[i], note.title ?? "", points, notePointLevels(note, points));
      });
    }
    if (list) {
      const next = list;
      setAgendaChecks((prev) => mergeAgendaItems(prev, next));
    }
    // `notes` changes identity every render; notesKey stands for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, draftId, key, items.data, items.isError, notesKey]);
}
